import React, { useMemo } from "react";

import Checkbox from "components/Common/Checkbox";
import styles from "./style.scss";
import { ToolTip } from "features/ToolTip";
import EmptyView from "components/Common/EmptyView";

interface Item {
  companyCode: string;
  companyName: string;
  stkCode: string;
  source: string;
  companyKbn?: string;
}

interface Props {
  items: Item[];
  selectedCorp: Item[];
  maxSelectableNumber: number;
  onSelect: (corp: Item[]) => void;
  disabledCodes?: string[];
}

export const CorporationTable: React.FC<Props> = ({
  items,
  selectedCorp,
  maxSelectableNumber,
  onSelect,
  disabledCodes = []
}) => {
  const onClick = (corp: Item, checked: boolean) => {
    if (disabledCodes.includes(corp.companyCode)) return;
    if (checked) {
      onSelect([...selectedCorp, corp]);
      return;
    }
    onSelect(
      selectedCorp.filter(item => item.companyCode !== corp.companyCode)
    );
  };

  // 全選択チェックボックスの状態
  const isAllChecked = useMemo(() => {
    // 無効化されている項目を省く
    const activeItems = items.filter(
      item => !disabledCodes.includes(item.companyCode)
    );
    // 項目が空の場合はチェックを入れない
    if (activeItems.length === 0) return false;

    return activeItems.every(item =>
      selectedCorp.find(corp => corp.companyCode === item.companyCode)
    );
  }, [items, selectedCorp, disabledCodes]);

  const isLimit = selectedCorp.length >= maxSelectableNumber;

  const onAllCheck = (checked: boolean) => {
    if (checked) {
      const newCorps = items.filter(
        corp =>
          !!!selectedCorp.find(tmp => tmp.companyCode === corp.companyCode) &&
          !disabledCodes.includes(corp.companyCode)
      );
      onSelect([...selectedCorp, ...newCorps]);
      return;
    }

    const excludeCorps = selectedCorp.filter(
      item => !!!items.find(tmp => tmp.companyCode === item.companyCode)
    );

    onSelect([...excludeCorps]);
  };

  const isSelectedEmpty = useMemo(() => {
    const selectedCompanyCodes = selectedCorp.map(item => item.companyCode);
    return !items.some(item => selectedCompanyCodes.includes(item.companyCode));
  }, [items, selectedCorp]);

  if (!items.length) return <EmptyView />;

  return (
    <div className={styles.tableContainer}>
      <table className={styles.vsTable}>
        <thead>
          <tr>
            <th data-testid="components-Tool-Screening-Sorporation-Item-Modern-Condition-Modal-CompanyModal-CorporationTable-allCheckbox">
              {isLimit && isSelectedEmpty ? (
                <ToolTip
                  position="right"
                  text={`項目選択数を${maxSelectableNumber}件以下にすることで選択可能になります。`}
                  tooltipWidth={354}
                  slideLength="0"
                >
                  <div className={styles.checkboxContainer}>
                    <Checkbox isChecked={false} isActive={false} />
                  </div>
                </ToolTip>
              ) : (
                <Checkbox
                  isChecked={isLimit || isAllChecked}
                  onChange={() => {
                    if (isLimit) {
                      onAllCheck(false);
                      return;
                    }
                    onAllCheck(!isAllChecked);
                  }}
                />
              )}
            </th>
            <th>株式コード</th>
            <th>ソース</th>
            <th>企業名</th>
          </tr>
        </thead>
        <tbody>
          {items.map((item, index) => {
            const target = selectedCorp.find(
              corp => corp.companyCode === item.companyCode
            );
            const isChecked = Boolean(target);
            const isDisabled = disabledCodes.includes(item.companyCode);
            return (
              <tr
                key={`recent-item-${index}`}
                data-testid="components-Tool-Screening-Sorporation-Item-Modern-Condition-Modal-CompanyModal-CorporationTable-tbodyRow"
                onClick={() => onClick(item, !target)}
                className={`${isDisabled ? styles.disabled : ""}`}
              >
                <td>
                  {!isLimit || isChecked || isDisabled ? (
                    <Checkbox isChecked={isChecked} isActive={!isDisabled} />
                  ) : (
                    <ToolTip
                      position="right"
                      text={`項目選択数を${maxSelectableNumber}件以下にすることで選択可能になります。`}
                      tooltipWidth={354}
                      slideLength="0"
                    >
                      <div className={styles.checkboxContainer}>
                        <Checkbox isChecked={isChecked} isActive={false} />
                      </div>
                    </ToolTip>
                  )}
                </td>
                <td>{item.stkCode}</td>
                <td>{item.source}</td>
                <td>{item.companyName}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
};
